import { useEffect, useRef } from 'react'
import { liveReadings } from '../state/live-readings'
import { amplitudeToDb, amplitudeToScalePercent } from '../lib/math'

/**
 * Bottom of the meter scale.
 *
 * Deliberately well below room noise: an input that is connected but silent
 * (a guitar interface with nothing plugged in sits near -90 dBFS) must still
 * read differently from one that delivers nothing at all.
 */
const FLOOR_DB = -90

/** Below this the input is effectively dead; worth telling the user. */
const NO_SIGNAL_DB = -70

/** Peaks at or above this are close enough to full scale to warn about. */
const CLIP_THRESHOLD = 0.98

/** Text next to the bar: the measured level, plus a warning where it matters. */
function readoutFor(rms: number, peak: number): string {
  if (rms <= 0) return 'keine Daten'

  const db = amplitudeToDb(rms, FLOOR_DB)
  const value = `${db.toFixed(1)} dBFS`
  if (peak >= CLIP_THRESHOLD) return `${value} · Übersteuerung`
  if (db <= NO_SIGNAL_DB) return `${value} · kein Signal`
  return value
}

/**
 * RMS bar with a peak marker, clipping and dead-input warnings.
 *
 * Reads from the live channel on each animation frame like the string bars do,
 * so 30 level messages a second cost no React renders.
 */
export function LevelMeter(): React.JSX.Element {
  const trackRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLSpanElement>(null)
  const peakRef = useRef<HTMLSpanElement>(null)
  const readoutRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    let handle = 0
    let lastText = ''
    let lastClipping: boolean | null = null

    const draw = (): void => {
      handle = requestAnimationFrame(draw)

      const { rms, peak } = liveReadings.snapshot().level
      const isClipping = peak >= CLIP_THRESHOLD

      // Scale from the left edge rather than animating width, so the
      // compositor can do this without a layout pass.
      if (fillRef.current) {
        fillRef.current.style.transform = `scaleX(${String(amplitudeToScalePercent(rms, FLOOR_DB) / 100)})`
      }
      if (peakRef.current) {
        peakRef.current.style.transform = `translateX(${String(amplitudeToScalePercent(peak, FLOOR_DB))}%)`
      }
      if (trackRef.current && lastClipping !== isClipping) {
        lastClipping = isClipping
        trackRef.current.dataset.clipping = isClipping ? 'true' : 'false'
        trackRef.current.setAttribute(
          'aria-valuenow',
          String(Math.round(amplitudeToScalePercent(rms, FLOOR_DB))),
        )
      }

      const text = readoutFor(rms, peak)
      if (readoutRef.current && lastText !== text) {
        lastText = text
        readoutRef.current.textContent = text
      }
    }

    handle = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(handle)
    }
  }, [])

  return (
    <div className="level">
      <div
        ref={trackRef}
        className="level__track"
        data-clipping="false"
        role="meter"
        aria-label="Eingangspegel"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={0}
      >
        <span ref={fillRef} className="level__fill" />
        <span ref={peakRef} className="level__peak" />
      </div>
      <span ref={readoutRef} className="level__readout">
        keine Daten
      </span>
    </div>
  )
}
